import React from 'react';
import '../assets/css/skeleton.css';

const Loader = ({
  rows = 3,
  type = 'line', // 'line', 'circle', 'box', 'card', 'custom'
  height = 20,
  width = '100%',
  borderRadius = 4,
  spacing = 10,
  animation = true,
  animationSpeed = 1.5,
  variant = 'default', // 'default', 'dark', 'light', 'gradient'
  className = '',
  style = {},
  rowStyles = [],
  layout = 'vertical', // 'vertical', 'horizontal'
  gridColumns = 1,
  randomWidths = false,
  center = false
}) => {

  const getVariantStyle = () => {
    const variants = {
      default: {
        baseColor: '#f0f0f0',
        highlightColor: '#e0e0e0'
      },
      dark: {
        baseColor: '#e0e0e0',
        highlightColor: '#d0d0d0'
      },
      light: {
        baseColor: '#f8f8f8',
        highlightColor: '#f0f0f0'
      },
      gradient: {
        baseColor: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 37%, #f0f0f0 63%)',
        highlightColor: '#f0f0f0'
      }
    };

    return variants[variant] || variants.default;
  };

  const getTypeStyle = (index) => {
    const baseStyle = {
      borderRadius: `${borderRadius}px`,
      background: getVariantStyle().baseColor,
      overflow: 'hidden',
      position: 'relative'
    };

    if (type === 'circle') {
      return {
        ...baseStyle,
        borderRadius: '50%',
        width: `${height}px`,
        height: `${height}px`,
        minWidth: `${height}px`
      };
    }

    if (type === 'box') {
      return {
        ...baseStyle,
        height: `${height}px`,
        width: `${height}px`,
        minWidth: `${height}px`
      };
    }

    if (type === 'card') {
      const cardHeight = height * 3;
      return {
        ...baseStyle,
        height: `${cardHeight}px`,
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
      };
    }

    // Custom or line type
    let finalWidth = width;
    if (randomWidths) {
      const randomPercent = 60 + Math.random() * 40;
      finalWidth = `${randomPercent}%`;
    }

    return {
      ...baseStyle,
      height: `${height}px`,
      width: finalWidth
    };
  };

  const renderSkeletonRow = (index) => {
    const variantStyle = getVariantStyle();
    const typeStyle = getTypeStyle(index);
    const customRowStyle = rowStyles[index] || {};

    if (type === 'card') {
      return (
        <div
          key={index}
          className={`skeleton-item skeleton-card ${animation ? 'shimmer' : ''} ${center ? 'center' : ''}`}
          style={{
            ...typeStyle,
            ...customRowStyle,
            '--base-color': variantStyle.baseColor,
            '--highlight-color': variantStyle.highlightColor,
            '--animation-speed': `${animationSpeed}s`,
            animationDuration: `${animationSpeed}s`,
            ...style
          }}
        >
          <div className="skeleton-card-header">
            <div className="skeleton-circle" style={{ width: '40px', height: '40px' }}></div>
            <div className="skeleton-line" style={{ width: '60%' }}></div>
          </div>
          <div className="skeleton-card-content">
            <div className="skeleton-line" style={{ width: '90%' }}></div>
            <div className="skeleton-line" style={{ width: '80%' }}></div>
            <div className="skeleton-line" style={{ width: '70%' }}></div>
          </div>
          <div className="skeleton-card-footer">
            <div className="skeleton-line" style={{ width: '40%' }}></div>
            <div className="skeleton-line" style={{ width: '20%' }}></div>
          </div>
        </div>
      );
    }

    return (
      <div
        key={index}
        className={`skeleton-item ${type} ${animation ? 'shimmer' : ''} ${center ? 'center' : ''}`}
        style={{
          ...typeStyle,
          ...customRowStyle,
          '--base-color': variantStyle.baseColor,
          '--highlight-color': variantStyle.highlightColor,
          '--animation-speed': `${animationSpeed}s`,
          animationDuration: `${animationSpeed}s`,
          ...style
        }}
      />
    );
  };

  const renderSkeletonGrid = () => {
    if (layout === 'horizontal') {
      return (
        <div className="skeleton-horizontal" style={{ gap: `${spacing}px` }}>
          {Array.from({ length: rows }).map((_, index) => renderSkeletonRow(index))}
        </div>
      );
    }

    if (gridColumns > 1) {
      return (
        <div
          className="skeleton-grid"
          style={{
            gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
            gap: `${spacing}px`
          }}
        >
          {Array.from({ length: rows }).map((_, index) => renderSkeletonRow(index))}
        </div>
      );
    }

    return (
      <div className="skeleton-vertical" style={{ gap: `${spacing}px` }}>
        {Array.from({ length: rows }).map((_, index) => renderSkeletonRow(index))}
      </div>
    );
  };

  return (
    <div className={`skeleton-loader ${className}`}>
      {renderSkeletonGrid()}
    </div>
  );
};

export default Loader;